import { Fragment, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import { FiBell, FiCheck, FiMail, FiSearch, FiSettings, FiTrash2, FiX } from "react-icons/fi";
import { useAdminAlerts } from "../../hooks/useAdminAlerts";
import { alertAccent, alertMatchesSearch, sortAlertsNewestFirst } from "../../utils/adminAlertsStore";

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

function ToggleRow({ label, checked, onChange, disabled = false }) {
  return (
    <label
      className={`flex items-center justify-between gap-3 py-2 ${
        disabled ? "opacity-50" : "cursor-pointer"
      }`}
    >
      <span className="text-xs text-gray-700">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative h-5 w-9 shrink-0 rounded-full transition ${
          checked ? "bg-[#FF2D38]" : "bg-gray-300"
        }`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow transition ${
            checked ? "translate-x-4" : ""
          }`}
        />
      </button>
    </label>
  );
}

export default function AdminNotificationsDropdown() {
  const navigate = useNavigate();
  const go = useMemo(() => (path) => navigate(path), [navigate]);
  const {
    alerts,
    unread,
    headerAlerts,
    headerUnread,
    prefs,
    updatePrefs,
    browserPermission,
    markRead,
    markUnread,
    markAllRead,
    clearHeaderList,
    requestBrowserPermission,
  } = useAdminAlerts({ navigate: go, enabled: true });
  const [showSettings, setShowSettings] = useState(false);
  const [search, setSearch] = useState("");
  const [readFilter, setReadFilter] = useState("all"); // all | unread | read

  const openAlert = (alert) => {
    markRead(alert.id);
    if (alert.route) navigate(alert.route);
  };

  const filteredAlerts = useMemo(() => {
    const list = sortAlertsNewestFirst(
      headerAlerts.filter((a) => {
        if (!alertMatchesSearch(a, search)) return false;
        if (readFilter === "unread" && a.read) return false;
        if (readFilter === "read" && !a.read) return false;
        return true;
      })
    );
    return list;
  }, [headerAlerts, search, readFilter]);

  const masterOn = prefs?.enabled !== false;
  const showBrowserPrompt =
    masterOn && prefs?.browser && browserPermission !== "granted";
  const searchActive = String(search || "").trim().length > 0;

  return (
    <Popover className="relative">
      <PopoverButton
        type="button"
        aria-label="Notifications"
        className="relative outline-none flex items-center justify-center h-10 w-10 rounded-full border border-rose-100 bg-white text-gray-700 hover:bg-rose-50 hover:text-[#FF2D38] transition shadow-sm"
      >
        <FiBell className="h-5 w-5" />
        {headerUnread > 0 ? (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF2D38] text-white text-[10px] font-bold flex items-center justify-center shadow">
            {headerUnread > 99 ? "99+" : headerUnread}
          </span>
        ) : null}
      </PopoverButton>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-150"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-100"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <PopoverPanel className="absolute right-0 z-[60] mt-3 w-[min(100vw-1.5rem,380px)] rounded-2xl border border-rose-100 bg-white shadow-2xl overflow-hidden">
          <div className="px-4 py-3 bg-gradient-to-r from-[#FF2D38] to-[#ff6b73] text-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-bold">Notifications</p>
                <p className="text-[11px] text-white/85 mt-0.5">
                  Support, chat, signups & verifications
                </p>
              </div>
              <div className="flex items-center gap-2">
                {headerUnread > 0 ? (
                  <span className="text-[10px] font-bold bg-white/20 px-2 py-0.5 rounded-full">
                    {headerUnread} new
                  </span>
                ) : null}
                <button
                  type="button"
                  aria-label="Notification settings"
                  onClick={() => setShowSettings((v) => !v)}
                  className="h-7 w-7 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
                >
                  {showSettings ? <FiX className="h-3.5 w-3.5" /> : <FiSettings className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>

          {showSettings ? (
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/90">
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500 mb-1">
                Notification settings
              </p>
              <p className="text-[10px] text-gray-500 mb-2">
                Applies to you on this browser only.
              </p>
              <ToggleRow
                label="All notifications"
                checked={masterOn}
                onChange={(enabled) => updatePrefs({ enabled })}
              />
              <ToggleRow
                label="Browser notifications"
                checked={!!prefs.browser}
                disabled={!masterOn}
                onChange={async (browser) => {
                  updatePrefs({ browser });
                  if (browser) await requestBrowserPermission();
                }}
              />
              <ToggleRow
                label="In-app toasts"
                checked={!!prefs.toast}
                disabled={!masterOn}
                onChange={(toastOn) => updatePrefs({ toast: toastOn })}
              />
              <div className="border-t border-gray-200 mt-2 pt-2">
                <p className="text-[10px] font-semibold text-gray-500 mb-1">Categories</p>
                <ToggleRow
                  label="Support tickets"
                  checked={prefs.types?.support_ticket !== false}
                  disabled={!masterOn}
                  onChange={(v) => updatePrefs({ types: { support_ticket: v } })}
                />
                <ToggleRow
                  label="New users"
                  checked={prefs.types?.user_registered !== false}
                  disabled={!masterOn}
                  onChange={(v) => updatePrefs({ types: { user_registered: v } })}
                />
                <ToggleRow
                  label="Verifications"
                  checked={prefs.types?.verification_pending !== false}
                  disabled={!masterOn}
                  onChange={(v) => updatePrefs({ types: { verification_pending: v } })}
                />
                <ToggleRow
                  label="Chat messages"
                  checked={prefs.types?.chat_message !== false}
                  disabled={!masterOn}
                  onChange={(v) => updatePrefs({ types: { chat_message: v } })}
                />
                <ToggleRow
                  label="Customer online"
                  checked={prefs.types?.user_online !== false}
                  disabled={!masterOn}
                  onChange={(v) => updatePrefs({ types: { user_online: v } })}
                />
              </div>
            </div>
          ) : null}

          {showBrowserPrompt ? (
            <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-2">
              <p className="text-[11px] text-amber-900 leading-snug">
                Enable browser alerts so you never miss a ticket or chat message.
              </p>
              <button
                type="button"
                onClick={() => requestBrowserPermission()}
                className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-amber-500 text-white hover:bg-amber-600"
              >
                Enable
              </button>
            </div>
          ) : null}

          <div className="px-3 py-2 border-b border-gray-100 bg-white space-y-2">
            <div className="relative">
              <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by user name or ID…"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-8 pr-8 py-1.5 text-xs text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-rose-300"
              />
              {searchActive ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FiX className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </div>
            <div className="flex items-center gap-1.5">
              {[
                { id: "all", label: "All" },
                { id: "unread", label: `Unread${headerUnread ? ` (${headerUnread})` : ""}` },
                { id: "read", label: "Read" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setReadFilter(tab.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                    readFilter === tab.id
                      ? "bg-[#FF2D38] text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50/80">
            <button
              type="button"
              onClick={clearHeaderList}
              disabled={!headerAlerts.length}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-[#FF2D38] disabled:opacity-40 disabled:hover:text-gray-600"
            >
              <FiTrash2 className="h-3.5 w-3.5" /> Clear all
            </button>
            <button
              type="button"
              onClick={markAllRead}
              disabled={headerUnread <= 0}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gray-600 hover:text-[#FF2D38] disabled:opacity-40 disabled:hover:text-gray-600"
            >
              <FiCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {!headerAlerts.length ? (
              <div className="px-6 py-10 text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 flex items-center justify-center text-xl mb-3">
                  🔔
                </div>
                <p className="text-sm font-semibold text-gray-800">You&apos;re all caught up</p>
                <p className="text-xs text-gray-500 mt-1">
                  {alerts.length
                    ? "This list is cleared. Full history is still in My Notifications."
                    : "New tickets, chats, registrations, and verification requests will appear here."}
                </p>
              </div>
            ) : !filteredAlerts.length ? (
              <div className="px-6 py-10 text-center">
                <p className="text-sm font-semibold text-gray-800">No matches</p>
                <p className="text-xs text-gray-500 mt-1">
                  {searchActive
                    ? `No notifications found for "${search.trim()}".`
                    : readFilter === "unread"
                      ? "No unread notifications."
                      : readFilter === "read"
                        ? "No read notifications."
                        : "No notifications found."}
                </p>
              </div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredAlerts.map((alert) => {
                  const accent = alertAccent(alert.type);
                  return (
                    <li key={alert.id}>
                      <div
                        className={`w-full text-left px-3.5 py-3 hover:bg-rose-50/60 transition ${
                          alert.read ? "bg-white" : "bg-rose-50/40"
                        }`}
                      >
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => openAlert(alert)}
                            className={`h-10 w-10 rounded-xl border ${accent.border} ${accent.bg} flex items-center justify-center text-lg shrink-0`}
                            aria-label="Open notification"
                          >
                            {accent.icon}
                          </button>
                          <div className="min-w-0 flex-1">
                            <button
                              type="button"
                              onClick={() => openAlert(alert)}
                              className="w-full text-left"
                            >
                              <div className="flex items-center gap-2 flex-wrap">
                                <span
                                  className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${accent.badge}`}
                                >
                                  {accent.label}
                                </span>
                                {!alert.read ? (
                                  <span className="h-1.5 w-1.5 rounded-full bg-[#FF2D38]" />
                                ) : null}
                                <span className="text-[10px] text-gray-400 ml-auto">
                                  {timeAgo(alert.receivedAt || alert.at)}
                                </span>
                              </div>
                              <p className="text-sm font-semibold text-gray-900 mt-1 leading-snug">
                                {alert.title}
                              </p>
                              {alert.message ? (
                                <p className="text-xs text-gray-600 mt-0.5 leading-snug line-clamp-2">
                                  {alert.message}
                                </p>
                              ) : null}
                              <p className="text-[10px] font-semibold text-[#FF2D38] mt-1.5">
                                Open →
                              </p>
                            </button>
                            <div className="mt-1.5 flex items-center gap-2">
                              {alert.read ? (
                                <button
                                  type="button"
                                  onClick={() => markUnread(alert.id)}
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-[#FF2D38]"
                                >
                                  <FiMail className="h-3 w-3" /> Mark unread
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => markRead(alert.id)}
                                  className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 hover:text-[#FF2D38]"
                                >
                                  <FiCheck className="h-3 w-3" /> Mark read
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-3 py-2.5 border-t border-gray-100 bg-white">
            <button
              type="button"
              onClick={() => navigate("/admin-alerts")}
              className="w-full text-center text-xs font-semibold text-[#FF2D38] hover:underline py-1"
            >
              View all my notifications →
            </button>
          </div>
        </PopoverPanel>
      </Transition>
    </Popover>
  );
}
